import { ExperiencePayload } from '../Types';
import FilterStore from '../../Builder/ExperienceCatalog/FilterTemplate/Store';
import UnlicensedToggleStore from './UnlicensedToggleStore';

// Programs with no set duration are represented as "Flexible" with this value
const FLEXIBLE_DURATION_VALUE = '0';

export const applyFilters = <T extends { readonly payload: ExperiencePayload }>(
  experiences: ReadonlyArray<T>,
  productTypeFilterStore: FilterStore,
  schoolPartnerFilterStore: FilterStore,
  languageFilterStore: FilterStore,
  durationFilterStore: FilterStore,
  licenseFilterStore: FilterStore,
  competencyFilterStore: FilterStore,
  unlicensedToggleStore: UnlicensedToggleStore,
): T[] => {
  return experiences.filter((exp: T) => {
    // Get selected filter values from stores
    // state.filter.options always contains the correct values (opened or closed)
    const selectedProductTypes = productTypeFilterStore.state.filter.options;
    const selectedSchoolPartners = schoolPartnerFilterStore.state.filter.options;
    const selectedLanguages = languageFilterStore.state.filter.options;
    const selectedDurations = durationFilterStore.state.filter.options;
    const selectedLicenses = licenseFilterStore.state.filter.options;
    const selectedCompetencies = competencyFilterStore.state.filter.options;

    // Apply filters with AND logic across filter types, OR logic within filters
    // Empty array means no filter selected = show all
    const productTypeMatch =
      selectedProductTypes.length === 0 || selectedProductTypes.includes(exp.payload.offering_type);

    const schoolPartnerMatch =
      selectedSchoolPartners.length === 0 ||
      selectedSchoolPartners.includes(exp.payload.school_partner_name);

    const languageMatch =
      selectedLanguages.length === 0 ||
      exp.payload.supported_languages.some((lang) => selectedLanguages.includes(lang));

    const durationMatch =
      selectedDurations.length === 0 ||
      exp.payload.total_hours
        .map((hours) => selectedDurations.includes(hours.toString()))
        .getOrElse(() => selectedDurations.includes(FLEXIBLE_DURATION_VALUE));

    // Determine if program is unlicensed (no license type IDs)
    const isUnlicensed = (exp.payload.product_license_type_ids || []).length === 0;

    // License filter - for licensed programs, check if any license type matches selected filters
    // Unlicensed programs bypass this filter (handled by unlicensedMatch below)
    // Empty array means show all (no filter applied)
    const licenseMatch =
      selectedLicenses.length === 0 ||
      isUnlicensed ||
      (exp.payload.product_license_type_ids || []).some((id: number) =>
        selectedLicenses.includes(id.toString()),
      );

    // Competency filter - check if any of the experience's competencies match selected filters
    // Empty array means show all (no filter applied)
    const competencyMatch =
      selectedCompetencies.length === 0 ||
      (exp.payload.competency_names || []).some((competency) =>
        selectedCompetencies.includes(competency),
      );

    // Unlicensed toggle filter - show unlicensed programs only if toggle is ON
    const unlicensedMatch = unlicensedToggleStore.showUnlicensed || !isUnlicensed;

    return (
      productTypeMatch &&
      schoolPartnerMatch &&
      languageMatch &&
      durationMatch &&
      licenseMatch &&
      competencyMatch &&
      unlicensedMatch
    );
  });
};
